/**
 * @file
 * Menu wizard script.
 */

(function themeMenuWizard($, Drupal, once) {
  'use strict';

  /**
   * Set the mode of the app.
   */
  function isSmallScreen() {
    const windowWidth = window.innerWidth || 0;
    return windowWidth < 1080;
  }

  /**
   * Submenu observer callback.
   *
   * Observe if submenu's full content is visible.
   * If the bottom isn't visible, add class "open--upward".
   * If the top is not visible, remove class "open--upward".
   */
  function submenuObserverCallback(entries) {
    entries.forEach((entry) => {
      const target = entry.target;
      const boundingRect = entry.boundingClientRect;
      const rootBounds = entry.rootBounds;
      const headerHeight = Drupal.ahsThemeSite.getStickyHeaderOuterHeight();

      if (!entry.isIntersecting) {
        target.classList.remove('open--upward');
      } else if (boundingRect.bottom > rootBounds.bottom &&
                (boundingRect.top - boundingRect.height) > headerHeight) {
        target.classList.add('open--upward');
      } else if (boundingRect.top < 0) {
        target.classList.remove('open--upward');
      }
    });
  }

  /**
   * Create single observer for the submenu items.
   */
  const submenuObserver = new IntersectionObserver(submenuObserverCallback, {
    // Root = browser viewport.
    root: null,
    threshold: [0, 1.0],
  });

  /**
   * Add observer for the submenu.
   *
   * @param {HTMLElement} element
   *   The element to observe.
   */
  function observeSubmenuPosition(element) {
    if (!(element instanceof Element)) {
      // console.error('observeSubmenuPosition: The provided element is not a valid DOM element.');
      return;
    }

    const pendingElements = once('themeMenuWizardSubmenuObserver', element);
    if (pendingElements.length) {
      submenuObserver.observe(pendingElements[0]);
    }
  }

  /**
   * Toggle the submenu-item.
   *
   * @param {jQuery} $subitems
   *   The submenu item element.
   * @param {Boolean} expand
   *   TRUE to force an expansion. If not provided then this function toggles
   *   the item based on its classes.
   */
  function toggleSubmenuItems($subitems, expand) {
    const needsCollapsed = typeof expand !== typeof undefined ? !expand : null;
    $subitems.get().forEach((subitem) => {
      const $subitem = $(subitem);
      const thisNeedsCollapsed = needsCollapsed !== null ? needsCollapsed : $subitem.hasClass('menu__submenu-item--expanded');

      if (thisNeedsCollapsed) {
        // Collapse the list.
        $subitem.removeClass('menu__submenu-item--expanded');
        $subitem.children('a').attr('aria-expanded', 'false');
      } else {
        // Close all others.
        $subitem
          .siblings('.menu__submenu-item')
          .removeClass('menu__submenu-item--expanded')
          .find('.menu__submenu-sub-headline-link')
          .attr('aria-expanded', 'false');
      }
    });
  }

  /**
   * Toggle the submenu open / close.
   *
   * @param {jQuery} $item
   *   The top level submenu item.
   * @param {Boolean} expand
   *   TRUE to force an expansion. If not provided then this function toggles
   *   the item based on its classes.
   */
  function toggleSubmenu($item, expand) {
    const $submenu = $item.children('.menu__submenu');
    if ($item.length === 0 || $submenu.length === 0) {
      return;
    }

    const $menuWizard = $item.parent('.menu--wizard');
    let $hiddenElements = $menuWizard.nextAll();
    $hiddenElements = $hiddenElements.add($menuWizard.siblings('.menu--wizard'));
    const needsCollapsed = typeof expand !== typeof undefined ? !expand : $item.hasClass('menu__item--submenu-expanded');
    const $closeButton = $('button.flyout-menu__close');

    if (needsCollapsed) {
      // Update the wizard.
      $menuWizard.removeClass('menu--wizard--expanded');
      // Close the submenu.
      $item.removeClass('menu__item--submenu-expanded');
      // Hide the submenu.
      $submenu.addClass('hidden');
      // Show the top level link.
      $item.children('a')
        // Update expanded state.
        .attr('aria-expanded', 'false');
      // Show all sibling items.
      $item.siblings().removeClass('hidden');
      // Close all items.
      toggleSubmenuItems($submenu.find('.menu__submenu-item'), false);

      // Mobile Menu.
      if (isSmallScreen()) {
        // Hide the top level link to so the user can use the back link.
        $item.children('.menu__item--submenu-toggle').removeClass('hidden');

        // Show the hidden elements.
        $hiddenElements.removeClass('hidden--important');
        $closeButton.off('keydown');
      }
      // Focus back on the top level link that expanded the menu.
      Drupal.ahsThemeSite.focus($item.children('a'));
    } else {
      // Update the wizard.
      $menuWizard.addClass('menu--wizard--expanded');
      // Open the submenu.
      $item.addClass('menu__item--submenu-expanded');
      // Top level link..
      $item.children('a')
        // Update expanded state.
        .attr('aria-expanded', 'true');
      // Show the submenu.
      $submenu.removeClass('hidden');
      // Convert $submenu to a DOM element and observe its position
      const submenuElement = $submenu.get(0);

      // move to init() ?.
      observeSubmenuPosition(submenuElement);

      // Mobile Menu.
      if (isSmallScreen()) {
        // Hide all sibling items.
        $item.siblings().addClass('hidden');
        // Hide the hidden elements.
        $hiddenElements.addClass('hidden--important');
        // Hide the top level link to so the user can use the back link.
        $item.children('.menu__item--submenu-toggle').addClass('hidden');
      }

      // Focus on the first focusable item in $submenu
      const $firstFocusableItem = $submenu.find('a, button, input, [tabindex]:not([tabindex="-1"])').filter(':visible').first();
      if ($firstFocusableItem.length) {
        $firstFocusableItem.focus();
      }
    }

    // Create a keyboard trap for $submenu
    const $focusableItems = $submenu.find('a, button, input, [tabindex]:not([tabindex="-1"])').filter(':visible');
    const $additionalFocusableItem = $closeButton;
    const $tabListenerItems = $submenu;
    if ($additionalFocusableItem.length && isSmallScreen()) {
      // Add $additionalFocusableItem to $tabListenerItems to track clicks.
      $tabListenerItems.push($additionalFocusableItem[0]);
    }
    const $firstItem = $focusableItems.first();
    const $lastItem = $focusableItems.last();

    $tabListenerItems.off('keydown.themeMenuWizard');
    $tabListenerItems.on('keydown.themeMenuWizard', (event) => {
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          // If Shift+Tab is pressed and focus is on the first item, move focus to the last item.
          if ($(document.activeElement).is($firstItem)) {
            event.preventDefault();
            $lastItem.focus();
          }
        } else if ($additionalFocusableItem.length && isSmallScreen()) {
          // If there is a flyout close button focus on it after last item then first item.
          if ($(document.activeElement).is($lastItem) && $focusableItems.length > 1) {
            event.preventDefault();
            $additionalFocusableItem.focus();
          } else if ($(document.activeElement).is($additionalFocusableItem)
            && $focusableItems.length > 1) {
            event.preventDefault();
            $firstItem.focus();
          }
        } else if ($(document.activeElement).is($lastItem) && $focusableItems.length > 1) {
          // If Tab is pressed and focus is on the last item, move focus to the first item
          event.preventDefault();
          $firstItem.focus();
        }
      }
    });
  }

  function submenuToggleClickKeydownListener(event) {
    if (event.type === 'keydown' && event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    const $toggle = $(event.currentTarget);
    // close other submenus
    const $openSubmenus = $toggle.parent('.menu__item--has-submenu').siblings('.menu__item--submenu-expanded');
    toggleSubmenu($openSubmenus, false);
    toggleSubmenu($toggle.parent('.menu__item--has-submenu'));
  }

  function submenuBackLinkClickKeydownListener(event) {
    if (event.type === 'keydown' && event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    const $backLink = $(event.currentTarget);
    toggleSubmenu($backLink.closest('.menu__item--has-submenu'), false);
  }

  /**
   * Setup submenu items.
   *
   * @param {HTMLElement[]} items
   *   The submenu items.
   */
  function initSubmenuItems(items) {
    items.forEach((item) => {
      const $item = $(item);

      // Top level link.
      $(once('themeMenuWizardSubmenuInitLinkClick', $item.children('.menu__item--submenu-toggle')))
        .on('click keydown', submenuToggleClickKeydownListener);

      // Back link.
      $(once('themeMenuWizardSubmenuInitBackLinkClick', $item.find('.menu__submenu-back-link')))
        .on('click keydown', submenuBackLinkClickKeydownListener);
    });
  }

  /**
   * Global Menu flyout.
   */
  Drupal.behaviors.themeMenuWizardSubmenu = {
    attach(context) {
      const items = once('themeMenuWizardSubmenu', '.menu__item--has-submenu', context);

      if (items.length) {
        initSubmenuItems(items);
      }
    },
  };
}(jQuery, Drupal, once));
